import { useEffect, useMemo } from "react";
import { Router } from "next/router";
import { isBefore, subDays } from "date-fns";
import { decode } from "jsonwebtoken";
import { isArray } from "lodash";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import {
  IClient,
  IContractBuilding,
  IModuleAccessMap,
  ISite,
} from "@/lib/interfaces";
import {
  disableLoading,
  setBoxInstances,
  setBuildings,
  setClients,
  setClientUsers,
  setContracts,
  setFavourite,
  setLastActiveInstanceTime,
  setLastActiveTime,
  setModuleAccessMap,
  setPrivileges,
  setRoles,
  setSites,
  setSubsystems,
  setUsers,
} from "@/lib/reducers/apiData";
import { initHiddenColumns } from "@/lib/reducers/common";
import { setUser } from "@/lib/reducers/user";
import { useGetUserDetailsQuery } from "@/lib/services/appApi";
import { useAgnosticGetQuery } from "@/lib/services/endpoints/newAPI/agnostic";
import {
  useGetClientBuildingsQuery,
  useGetClientContractsQuery,
  useGetClientRolesQuery,
} from "@/lib/services/endpoints/newAPI/customQueries";
import { useEnsurePrivileges } from "@/lib/services/privilegeConfiguration";
import { AppPayload } from "@/lib/types";
import { useFallbackFavourite } from "./useFallbackFavourite";

interface Props {
  router: Router;
}

const DataLoader = ({ router }: Props) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(initHiddenColumns());
  }, [dispatch]);

  const accessToken = useAppSelector((state) => state.user.accessToken);
  const {
    loading,
    clients: rdxClients,
    buildings: rdxBuildings,
    contracts: rdxContracts,
  } = useAppSelector((state) => state.apiData);
  const { building, contract, client } = useAppSelector(
    (state) => state.apiData.favourite
  );

  //Get Instances
  const {
    data: boxInstancesData,
    isLoading: isLoadingBI,
    isFetching: isFetchingBI,
  } = useAgnosticGetQuery(
    {
      url: `/client/${client}/boxInstances`,
      params: { complete: "1" },
    },
    {
      skip: client === undefined || client === "" || accessToken === "",
    }
  );

  const {
    data: lastActiveTimeData,
    isLoading: isLoadingLA,
    isFetching: isFetchingLA,
  } = useAgnosticGetQuery(
    {
      url: `/client/${client}/module/sensor/lastActiveTime`,
    },
    {
      skip: !client || client === "" || accessToken === "" || client == "0",
    }
  );

  //GET USER — derive inner JWT token field from accessToken
  const decodedToken = useMemo(() => {
    if (!accessToken) return "";
    try {
      const decoded = decode(accessToken) as AppPayload | null;
      return decoded?.email && decoded?.token ? decoded.token : "";
    } catch {
      return "";
    }
  }, [accessToken]);

  const userQuery = useGetUserDetailsQuery(
    { accessToken: decodedToken },
    { skip: !decodedToken }
  );

  //GET Subsystems
  const subsystemsQuery = useAgnosticGetQuery(
    { url: "/subsystems" },
    { skip: !accessToken }
  );

  //GET Users ONCE!
  const { data: usersData } = useAgnosticGetQuery(
    {
      url: "/users",
      params: {
        complete: "1",
        fields: [
          "username",
          "name",
          "firstname",
          "lastname",
          "primaryClient",
          "active",
          "superuser",
          "email",
          "tmPosition",
          "fileHash",
          "selfie",
          "authorisedPerson",
          "includeAsPersonnel",
          "tmBuildingsSites",
          "tmPosition",
        ],
      },
    },
    { skip: accessToken === "" }
  );

  useEffect(() => {
    if (isArray(usersData?.data)) {
      dispatch(disableLoading({ type: "users" }));
      dispatch(
        setUsers({
          data: usersData.data,
        })
      );
    }
  }, [usersData, dispatch]);

  //GET client users ONCE!
  const { data: clientUsersData } = useAgnosticGetQuery(
    {
      url: `/client/${client}/users`,
      params: {
        complete: "1",
        fields: [
          "username",
          "name",
          "firstname",
          "lastname",
          "primaryClient",
          "active",
          "superuser",
          "email",
          "tmPosition",
          "fileHash",
          "selfie",
          "authorisedPerson",
          "includeAsPersonnel",
          "tmBuildingsSites",
          "tmPosition",
        ],
      },
    },
    { skip: accessToken === "" || client === "0" }
  );

  useEffect(() => {
    if (isArray(clientUsersData?.data)) {
      dispatch(disableLoading({ type: "clientUsers" }));
      dispatch(
        setClientUsers({
          data: clientUsersData.data,
        })
      );
    }
  }, [clientUsersData, dispatch]);

  //GET Clients
  const clients = useAgnosticGetQuery(
    {
      url: "/clients",
      params: { complete: 1 },
    },
    { skip: !accessToken }
  );

  useEffect(() => {
    if (!clients.isUninitialized && !clients.isLoading && clients.data) {
      dispatch(setClients({ data: clients.data.data }));
      dispatch(disableLoading({ type: "clients" }));
    }
  }, [clients.isUninitialized, clients.isLoading, clients.data, dispatch]);

  //Tidy up client favourite:
  useFallbackFavourite("client", rdxClients, client);

  // Dispatch user when both userQuery and clients are resolved
  useEffect(() => {
    if (
      userQuery.isUninitialized ||
      userQuery.isLoading ||
      clients.isUninitialized ||
      clients.isLoading
    ) {
      return;
    }
    if (userQuery.data?.status === "success") {
      const userData = userQuery.data.data;
      const findCompany: undefined | IClient = clients.data?.data.find(
        (item: IClient) => item.id === userData.primaryClient
      );
      dispatch(setUser({ user: userData, company: findCompany }));
    }
  }, [
    userQuery.isUninitialized,
    userQuery.isLoading,
    userQuery.data,
    clients.isUninitialized,
    clients.isLoading,
    clients.data,
    dispatch,
  ]);

  // Dispatch subsystems when query succeeds
  useEffect(() => {
    if (
      subsystemsQuery.isSuccess &&
      subsystemsQuery.data.status === "success"
    ) {
      dispatch(setSubsystems({ subsystems: subsystemsQuery.data.data }));
      dispatch(disableLoading({ type: "subsystems" }));
    }
  }, [subsystemsQuery.isSuccess, subsystemsQuery.data, dispatch]);

  // GET Contracts — derive ids from clients, then dispatch when query resolves
  const contractIds = useMemo<Array<string>>(() => {
    if (!clients.data?.data) return [];
    return clients.data.data
      .filter((item) => !(+item.isSupplier === 1 && +item.isContracted === 0))
      .map((item) => item.id);
  }, [clients.data]);

  const contractsQuery = useGetClientContractsQuery(contractIds, {
    skip: contractIds.length === 0,
  });

  useEffect(() => {
    if (contractsQuery.isSuccess) {
      dispatch(setContracts({ data: contractsQuery.data.contracts }));
      dispatch(disableLoading({ type: "contracts" }));
    }
  }, [contractsQuery.isSuccess, contractsQuery.data, dispatch]);

  //Tidy up contracts favourite:
  useFallbackFavourite("contract", rdxContracts, contract);

  // GET Buildings -- for client not contract
  const buildingsQuery = useGetClientBuildingsQuery(rdxClients, {
    skip: loading.clients === true,
  });

  useEffect(() => {
    if (buildingsQuery.isSuccess) {
      dispatch(
        setBuildings({
          //only active buildings
          // data: buildingsQuery.data.buildings
          data: buildingsQuery.data.buildings?.filter(
            (b) => b.isActive === "1"
          ),
        })
      );
      dispatch(
        disableLoading({
          type: "buildings",
        })
      );
    }
  }, [buildingsQuery, dispatch]);

  //Tidy up building favourite:
  useFallbackFavourite("building", rdxBuildings, building);

  //Auto set building when client changes?
  useEffect(() => {
    if (client && building && rdxBuildings) {
      const findBuilding = rdxBuildings.find((c) => c.id == building);
      if (findBuilding?.clientId != client) {
        const clientBuilding = rdxBuildings.find((b) => b.clientId == client);
        if (clientBuilding) {
          dispatch(
            setFavourite({ type: "building", value: clientBuilding.id })
          );
        }
      }
    }
  }, [client, building, rdxBuildings, dispatch]);

  // GET Roles -- for client
  const clientRolesQuery = useGetClientRolesQuery(rdxClients, {
    skip: loading.clients === true,
  });

  useEffect(() => {
    if (clientRolesQuery.isSuccess) {
      dispatch(
        setRoles({
          data: clientRolesQuery.data.roles,
        })
      );
      dispatch(
        disableLoading({
          type: "roles",
        })
      );
    }
  }, [clientRolesQuery, dispatch]);

  const grantedPrivilegesQuery = useAgnosticGetQuery(
    {
      url: `/client/${client}/grantedPrivileges`,
      params: { complete: "1" },
    },
    {
      skip:
        accessToken === "" || client === "0" || client === undefined || !client,
    }
  );

  //missing accessToken === '' on skip caused weird bug, unable to automatically reload modules then log in/out on diff users
  const { ensurePrivileges } = useEnsurePrivileges();

  useEffect(() => {
    const { data, isSuccess } = grantedPrivilegesQuery;

    if (isSuccess && data.status === "success") {
      dispatch(disableLoading({ type: "privileges" }));
      dispatch(setPrivileges({ privileges: data.data }));

      const lastRefresh = new Date(+data.lastRefresh! * 1000);
      const compareDate = subDays(new Date(), 1);

      if (isBefore(lastRefresh, compareDate)) {
        ensurePrivileges();
      }
    }
  }, [
    grantedPrivilegesQuery,
    dispatch,
    // ensurePrivileges
  ]);

  //New init data
  const {
    data: contractSubsystemsData,
    isLoading: isCSLoading,
    isFetching: isCSFetching,
  } = useAgnosticGetQuery(
    {
      url: `/util/getContractSubsystems`,
      params: { complete: "1" },
    },
    {
      skip: accessToken === "",
    }
  );

  const {
    data: contractBuildingsData,
    isLoading: isCBLoading,
    isFetching: isCBFetching,
  } = useAgnosticGetQuery(
    {
      url: `/util/getContractBuildings`,
      params: { complete: "1" },
    },
    {
      skip: accessToken === "",
    }
  );

  useEffect(() => {
    const res: IModuleAccessMap = {};
    if (
      contractSubsystemsData?.data?.length > 0 &&
      !isCSLoading &&
      !isCSFetching &&
      contractBuildingsData?.data?.length > 0 &&
      !isCBLoading &&
      !isCBFetching
    ) {
      //1. Build client-contract-building map
      contractBuildingsData?.data?.map((item: IContractBuilding) => {
        if (!res[item.client]) {
          res[item.client] = {};
        }

        if (!res[item.client][item.contract]) {
          res[item.client][item.contract] = {
            buildings: [],
            subsystems: [],
          };
        }
        if (item.available == "1") {
          res[item.client][item.contract]["buildings"].push(item.building);
        }
      });
      //2. Put subsystems in the map
      Object.keys(res).map((clientId) => {
        Object.keys(res[clientId]).map((contractId) => {
          contractSubsystemsData?.data?.map((item) => {
            if (item.client === clientId && item.contract === contractId) {
              const findSubsystem = subsystemsQuery?.data?.data?.find(
                (subsystem) => subsystem.id == item.subsystem
              );
              res[clientId][contractId]["subsystems"].push(findSubsystem);
            }
          });
        });
      });

      dispatch(setModuleAccessMap({ moduleAccessMap: res }));
    }
  }, [
    contractSubsystemsData,
    isCSLoading,
    isCSFetching,
    contractBuildingsData,
    isCBLoading,
    isCBFetching,
    subsystemsQuery?.data?.data,
    dispatch,
  ]);
  //------------

  const {
    data: buildingGroupData,
    isLoading,
    isFetching,
  } = useAgnosticGetQuery(
    {
      url: `/util/getBuildingGroups`,
      params: { complete: "1" },
    },
    {
      skip: accessToken === "",
    }
  );

  useEffect(() => {
    if (rdxBuildings.length > 0 && !isLoading && !isFetching) {
      const tempSites: ISite[] = [];
      buildingGroupData?.data?.map((item) => {
        const site = {
          id: item.id,
          name: item.name,
          buildings: [],
          client: item.client,
        };

        try {
          JSON.parse(item.buildings)?.map((bId: string) => {
            const findBuilding = rdxBuildings.find((b) => b.id === bId);
            if (findBuilding) {
              site.buildings.push(findBuilding);
            }
          });
        } catch (e) {}
        tempSites.push(site);
      });
      dispatch(setSites({ sites: tempSites }));

      //Set default site
      let siteId = 0;
      tempSites?.map((site) => {
        site?.buildings?.map((item) => {
          if (item.id == building) {
            siteId = +site.id;
          }
        });
      });
      if (siteId > 0) {
        dispatch(setFavourite({ type: "site", value: siteId }));
      }
    }
  }, [
    rdxBuildings,
    buildingGroupData,
    isLoading,
    isFetching,
    building,
    dispatch,
  ]);

  //Redirect new Induction user
  useEffect(() => {
    if (accessToken !== "" && !loading.buildings) {
      if (rdxBuildings.length === 0) {
        router.push("/module/inductions");
      }
    }
  }, [rdxBuildings, accessToken, loading.buildings]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isFetchingLA || isLoadingLA) return;

    if (lastActiveTimeData?.status === "success") {
      const lastActiveTime = new Date(+lastActiveTimeData.time * 1000);
      dispatch(setLastActiveTime({ lastActiveTime }));
    }
  }, [lastActiveTimeData, isLoadingLA, isFetchingLA, dispatch]);

  useEffect(() => {
    if (isLoadingBI || isFetchingBI) return;
    if (boxInstancesData?.status === "success") {
      dispatch(setBoxInstances({ data: boxInstancesData?.data }));
    }
  }, [boxInstancesData, isFetchingBI, isLoadingBI, dispatch]);

  //Get last active instance(s) by building time
  useEffect(() => {
    let lastActiveInstanceTime = 0;
    if (isLoadingBI || isFetchingBI) return;
    if (
      boxInstancesData?.status === "success" &&
      lastActiveTimeData?.status === "success"
    ) {
      let filteredInstances = boxInstancesData?.data
        .filter((i) => i.excludeFromCollectorOfflineChecks == "0")
        .filter((i) => i.defaultBuilding === building);

      if (filteredInstances.length === 0) {
        filteredInstances = boxInstancesData?.data.filter(
          (i) => i.defaultBuilding === building
        );
      }

      filteredInstances?.map((instance) => {
        const { lastUpdate } = instance;
        if (+lastUpdate > lastActiveInstanceTime)
          lastActiveInstanceTime = +lastUpdate;
      });

      if (filteredInstances.length === 0 && lastActiveTimeData?.time) {
        lastActiveInstanceTime = +lastActiveTimeData.time;
      }

      if (lastActiveInstanceTime > 0) {
        dispatch(
          setLastActiveInstanceTime({
            lastActiveInstanceTime: new Date(lastActiveInstanceTime * 1000),
          })
        );
      }
    }
  }, [
    boxInstancesData,
    isFetchingBI,
    isLoadingBI,
    building,
    dispatch,
    lastActiveTimeData,
  ]);

  return <></>;
};

export default DataLoader;
